import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Globe2, MapPin, Users, UserCircle2, Briefcase } from "lucide-react";
import { WAF_COUNTRIES } from "@/data/waf-region";

/**
 * Compact "Informations client" form rendered at the top of the Configurator.
 * The captured fields are forwarded to ResultsView (so the Outlook draft is
 * pre-filled) and persisted with the saved quote so the same draft can be
 * regenerated from "Mes Devis" without re-asking the partner.
 *
 * Default values:
 *   - partner_name → user.company || ""
 *   - generated_by → user.full_name || user.email
 *   - generated_by_email → user.email
 */
export default function CustomerInfoForm({ value, onChange, user, dataTestId = "customer-info-form" }) {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });

  return (
    <div className="border border-zinc-200 bg-white rounded-sm" data-testid={dataTestId}>
      <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-[#D42B1E]" />
          <h3 className="text-sm font-semibold tracking-[0.15em] uppercase text-zinc-500">
            Informations client &amp; partenaire
          </h3>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">
          Saisie figée avec le devis
        </span>
      </div>

      <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Field label="Nom du client final" icon={Building2}>
          <Input
            placeholder="ex. Banque Atlantique"
            value={v.name || ""}
            onChange={(e) => set("name", e.target.value)}
            className="rounded-sm"
            data-testid="customer-info-name"
          />
        </Field>

        <Field label="Pays du client" icon={Globe2}>
          <Select value={v.country || ""} onValueChange={(val) => set("country", val)}>
            <SelectTrigger className="rounded-sm" data-testid="customer-info-country">
              <SelectValue placeholder="Choisir un pays WAF" />
            </SelectTrigger>
            <SelectContent>
              {WAF_COUNTRIES.map((c) => (
                <SelectItem key={c.iso2} value={c.name} data-testid={`customer-info-country-${c.iso2}`}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Siège du client" icon={MapPin}>
          <Input
            placeholder="ex. Plateau, Abidjan"
            value={v.headquarters || ""}
            onChange={(e) => set("headquarters", e.target.value)}
            className="rounded-sm"
            data-testid="customer-info-headquarters"
          />
        </Field>

        <Field label="Agence(s) à équiper" icon={Users}>
          <Input
            placeholder="ex. Yopougon, Cocody, Bouaké"
            value={v.branch || ""}
            onChange={(e) => set("branch", e.target.value)}
            className="rounded-sm"
            data-testid="customer-info-branch"
          />
        </Field>

        <Field label="Partenaire intégrateur" icon={Briefcase}>
          <Input
            placeholder="ex. ACME Networks"
            value={v.partner_name || ""}
            onChange={(e) => set("partner_name", e.target.value)}
            className="rounded-sm"
            data-testid="customer-info-partner-name"
          />
        </Field>

        <Field label="BOM/BOQ généré par" icon={UserCircle2}>
          <Input
            placeholder={user?.full_name || user?.email || "Votre nom"}
            value={v.generated_by || ""}
            onChange={(e) => set("generated_by", e.target.value)}
            className="rounded-sm"
            data-testid="customer-info-generated-by"
          />
        </Field>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <Label className="text-xs text-zinc-600 flex items-center gap-1.5 mb-1">
        {Icon && <Icon className="h-3.5 w-3.5" />} {label}
      </Label>
      {children}
    </div>
  );
}
